'use strict'

/**
 * ダッシュボード×項目マトリクス画面のフィーチャー単位 Redux ストア。 単一 state ツリー（モード・対象期間・マッピング・フィルタなど）を保持し、 サブコンポーネントは useDashboardMatrixSelector
 * でスライスを直接購読する（props でのバケツリレーをしない）。 reducer は immer の produce で「コピーしてから差し替え」を隠す。
 */

import { produce } from 'immer'
import React, { useCallback } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { createStore } from 'redux'
import { MODE_STANDALONE, PERIOD_YEAR } from './constants'

/** アクションタイプ定数 */
export const ACTION_TYPES = {
  SET_MODE: 'SET_MODE', // 表示モード切替（単体 / 連結＆セグメント）
  SET_PERIOD_KIND: 'SET_PERIOD_KIND', // 集計期間種別切替（月次 / 四半期 / 年次）。detailPeriodKey を併送する
  SET_DETAIL_PERIOD: 'SET_DETAIL_PERIOD', // 詳細期間（タスク）の選択
  INIT: 'INIT', // 画面初期化（API 取得後などで全データを一括セット）
  SET_MAPPING: 'SET_MAPPING', // 汎用マッピング一覧の更新（主のみ。月次参照はクリア）
  SET_DUAL_MAPPING: 'SET_DUAL_MAPPING', // 主＋月次参照用マッピングを一括更新
  SET_MAPPING_ERROR: 'SET_MAPPING_ERROR', // マッピング取得エラー
  SET_MAPPING_LOADING: 'SET_MAPPING_LOADING', // マッピング取得中フラグ
  SET_TASK_LIST: 'SET_TASK_LIST', // タスク一覧の更新
  SET_FILTERS: 'SET_FILTERS', // 検索・チェック・行フィルタの更新
  RESET_STATE: 'RESET_STATE' // 初期 state に戻す
}

/** 初期 state（左列・横列・マッピング・フィルタの初期値） */
export const initialState = {
  mode: MODE_STANDALONE,
  periodKind: PERIOD_YEAR,
  detailPeriodKey: null,
  settingJson: null,
  leftColumnRowsFull: [],
  horizontalBlocks: [],
  taskListFull: [],
  mappingList: [],
  mappingListMonthly: [],
  mappingError: null,
  mappingLoading: false,
  loading: true,
  error: null,
  apiWarning: null,
  filters: {
    searchText: '',
    checkedSet: false,
    checkedUnset: false,
    rowFilters: {
      major: [],
      middle: [],
      dashboard: [],
      graph: [],
      referable: 'all'
    }
  }
}

/**
 * Reducer: action に応じて draft を更新し、新しい state を返す。 旧 extra/reducer.js のロジックをそのまま移植している。
 *
 * @param {typeof initialState} state - 現在の state
 * @param {{ type: string; payload?: any; detailPeriodKey?: number | null }} action
 * @returns {typeof initialState} 更新後の state（Immer が生成）
 */
export const dashboardMatrixReducer = (state = initialState, action) => {
  if (action.type === ACTION_TYPES.RESET_STATE) {
    return initialState
  }
  return produce(state, (draft) => {
    switch (action.type) {
      case ACTION_TYPES.SET_MODE:
        draft.mode = action.payload
        break
      case ACTION_TYPES.SET_PERIOD_KIND:
        draft.periodKind = action.payload
        draft.detailPeriodKey = action.detailPeriodKey ?? null
        if (draft.detailPeriodKey == null) {
          draft.mappingList = []
          draft.mappingListMonthly = []
          draft.mappingError = null
          draft.mappingLoading = false
        }
        break
      case ACTION_TYPES.SET_DETAIL_PERIOD: // 詳細期間変更時も mapping をクリア
        draft.detailPeriodKey = action.payload
        if (draft.detailPeriodKey == null) {
          draft.mappingList = []
          draft.mappingListMonthly = []
          draft.mappingError = null
          draft.mappingLoading = false
        }
        break
      case ACTION_TYPES.INIT: // 画面初期化：設定・左列・横列・タスク・マッピング・loading/error を一括セット
        draft.settingJson = action.payload.settingJson
        draft.leftColumnRowsFull = action.payload.leftColumnRowsFull
        draft.horizontalBlocks = action.payload.horizontalBlocks
        draft.taskListFull = action.payload.taskListFull
        draft.periodKind = action.payload.periodKind ?? PERIOD_YEAR
        draft.detailPeriodKey = action.payload.detailPeriodKey ?? null
        draft.mappingList = action.payload.mappingList ?? []
        draft.mappingListMonthly = action.payload.mappingListMonthly ?? []
        draft.mappingError = action.payload.mappingError ?? null
        draft.mappingLoading = action.payload.mappingLoading ?? false
        draft.loading = false
        draft.error = action.payload.error ?? null
        draft.apiWarning = action.payload.apiWarning ?? null
        break
      case ACTION_TYPES.SET_MAPPING:
        draft.mappingList = action.payload
        draft.mappingListMonthly = []
        draft.mappingError = null
        draft.mappingLoading = false
        break
      case ACTION_TYPES.SET_DUAL_MAPPING:
        draft.mappingList = action.payload.mappingList ?? []
        draft.mappingListMonthly = action.payload.mappingListMonthly ?? []
        draft.mappingError = null
        draft.mappingLoading = false
        break
      case ACTION_TYPES.SET_MAPPING_ERROR:
        draft.mappingError = action.payload
        draft.mappingLoading = false
        draft.mappingList = []
        draft.mappingListMonthly = []
        break
      case ACTION_TYPES.SET_MAPPING_LOADING:
        draft.mappingLoading = action.payload
        break
      case ACTION_TYPES.SET_TASK_LIST:
        draft.taskListFull = action.payload
        break
      case ACTION_TYPES.SET_FILTERS: {
        // フィルタ更新（rowFilters は既存と merge）
        const prevRowFilters = draft.filters.rowFilters
        draft.filters = { ...draft.filters, ...action.payload }
        if (action.payload.rowFilters != null) {
          draft.filters.rowFilters = { ...prevRowFilters, ...action.payload.rowFilters }
        }
        break
      }
      default:
        break
    }
  })
}

/**
 * Dispatch をラップして `dispatch(type, payload, extra)` 形式で呼べるようにする。 SET_PERIOD_KIND のように payload
 * 以外の付加フィールド（detailPeriodKey）を 送る場合は第3引数 extra に渡す（例: dispatch(ACTION_TYPES.SET_PERIOD_KIND, periodKind, {
 * detailPeriodKey })）。
 *
 * @returns {(type: string, payload?: any, extra?: object) => void}
 */
export const useDashboardMatrixDispatch = () => {
  const _dispatch = useDispatch()
  // 参照を安定させる（useEffect の依存配列やメモ化ハンドラの依存に入れても無限ループにならないように）。
  return useCallback((type, payload, extra) => _dispatch({ type, payload, ...extra }), [_dispatch])
}

/** @type {import('react-redux').TypedUseSelectorHook<typeof initialState>} */
export const useDashboardMatrixSelector = useSelector

/**
 * フィーチャーストアの Provider。Content をこの Provider で包む。 corporationNumber / accPeriod はメインストアの値なので、この Provider の 外側で読み取り props
 * で渡すこと（入れ子 Provider 下では useSelector がフィーチャーストアを参照するため）。
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const DashboardMatrixProviderStore = (props) => {
  const [store] = React.useState(() => createStore(dashboardMatrixReducer))
  return <Provider store={store}>{props.children}</Provider>
}
